import React, { useState, useRef } from "react";
import { AIMetadata, AIOutput, AIResponseType } from "system/AIResponseTypes";
import { AILoader } from "./AILoader";
import { Box, IconButton, Tooltip } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { Query } from "system/query/Query";
import { AISummaryRenderer } from "components/widgets/ai/AISummary/AISummaryRenderer";
import { AIPersonasRenderer } from "components/widgets/ai/AISocialPersonas/AIPersonasRenderer";
import { usePDFExport } from 'utils/usePDFExport';
import { useTranslation } from "react-i18next";
import WidgetPDFDownloadDialog from "./exports/WidgetPDFDownloadDialog";

export default interface AIStandaloneLoaderProps {
  queryIds?: string[];
  queryBody?: any;
  responseType: AIResponseType;

  showRerunButton?: boolean;

  autoLoad?: boolean;
}

export function AIStandaloneLoader(props: AIStandaloneLoaderProps) {
  const { i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [metadata, setMetadata] = useState<AIMetadata | undefined>(undefined);
  const [data, setData] = useState<AIOutput | undefined>(undefined);

  const [drilldownTitle, setDrilldownTitle] = useState<string | undefined>();
  const [drilldownQuery, setDrilldownQuery] = useState<Query | undefined>();

  const exportContentRef = useRef<HTMLDivElement>(null);
  const { downloadPDF } = usePDFExport();
  const [showPDFDialog, setShowPDFDialog] = useState(false);

  const downloadPDFButton = (
    <Box sx={{ position: "absolute", top: "15px", right: "15px" }} > 
      <Tooltip title={i18n.t("export-PDF")}>
        <IconButton
          size='medium'
          disabled={data?.complete !== true}
          onClick={() => setShowPDFDialog(true)}>
          <PictureAsPdf />
        </IconButton>
      </Tooltip>
    </Box>  
  );

  return (
    <AILoader
      queryIds={props.queryIds}
      queryBody={props.queryBody}
      responseType={props.responseType}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      isStreaming={isStreaming}
      setIsStreaming={setIsStreaming}
      metadata={metadata}
      setMetadata={setMetadata}
      data={data}
      setData={setData}
      canSaveWidgetCache={false}
      autoLoad={props.autoLoad}
      showRerunButton={props.showRerunButton} >
        {data && <Box sx={{ position: "relative" }}>{downloadPDFButton}</Box>}
        {props.responseType === AIResponseType.SUMMARY &&
          <AISummaryRenderer
            data={data as AIOutput}
            metadata={metadata as AIMetadata}
            setDrilldownQuery={setDrilldownQuery}
            setDrilldownTitle={setDrilldownTitle}
            exportContentRef={exportContentRef}
          />
        }
        {props.responseType === AIResponseType.PERSONAS &&
          <AIPersonasRenderer
            data={data as AIOutput}
            metadata={metadata as AIMetadata}
            setDrilldownQuery={setDrilldownQuery}
            setDrilldownTitle={setDrilldownTitle}
            exportContentRef={exportContentRef}
          />
        }
        {showPDFDialog &&
          <WidgetPDFDownloadDialog
            defaultFilename={i18n.t("widgets.default-pdf-ai-filename")}
            onDownloadPDF={(options) => downloadPDF(exportContentRef, options)}
            onClose={() => setShowPDFDialog(false)}
          />
        }
    </AILoader>
  );
}